import { useMutation } from "@tanstack/react-query";
import { type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { api, unwrap } from "@/api/client";
import { pathForStep, useSetSession } from "@/auth/session";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorMessage } from "@/i18n";

import { AuthLayout } from "./auth-layout";

/** The second step of sign-in: a code from the app, or a recovery code. */
export function EnterCodePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useSetSession();
  const [recovery, setRecovery] = useState(false);
  const [value, setValue] = useState("");
  const [missing, setMissing] = useState(false);
  const verify = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/auth/mfa/verify", {
          body: recovery ? { recoveryCode: value.trim() } : { code: value.trim() },
        }),
      ),
    onSuccess: (session) => {
      setSession(session);
      void navigate(pathForStep(session.nextStep), { replace: true });
    },
  });

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    setMissing(!value.trim());
    if (value.trim()) {
      verify.mutate();
    }
  };

  return (
    <AuthLayout
      title={t("code.title")}
      intro={recovery ? t("code.recoveryIntro") : t("code.intro")}
    >
      <form noValidate className="flex flex-col gap-5" onSubmit={submit}>
        {verify.isError ? <Alert tone="error">{errorMessage(verify.error)}</Alert> : null}
        <Field
          label={recovery ? t("code.recoveryLabel") : t("code.label")}
          inputMode={recovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          error={missing ? t(recovery ? "code.recoveryRequired" : "code.codeRequired") : undefined}
        />
        <Button type="submit" disabled={verify.isPending}>
          {t("code.submit")}
        </Button>
        <Button
          variant="link"
          onClick={() => {
            setRecovery(!recovery);
            setValue("");
            setMissing(false);
          }}
        >
          {recovery ? t("code.useApp") : t("code.useRecovery")}
        </Button>
      </form>
    </AuthLayout>
  );
}
